import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  createNewsAlert,
  getNewsAlerts,
  deleteNewsAlert,
  toggleNewsAlert
} from '../utils/newsApiService';
import './NewsAlertPage.css';

const NewsAlertPage = () => {
  const { user, isAuthenticated } = useAuth();
  
  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    keyword: '',
    frequency: 'hourly',
    alertType: 'email',
    alertInfo: '',
    isActive: true,
    timeSlots: [] // 시간대 배열 추가
  });

  // 시간대 입력 상태
  const [currentTimeSlot, setCurrentTimeSlot] = useState({
    startTime: '09:00',
    endTime: '09:59'
  });

  // 시간대 관련 UI 상태
  const [timeSlotError, setTimeSlotError] = useState('');

  // UI 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [registeredAlerts, setRegisteredAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 컴포넌트 마운트 시 기존 알림 목록 조회
  useEffect(() => {
    if (isAuthenticated) {
      fetchRegisteredAlerts();
    }
  }, [isAuthenticated]);

  // 기존 알림 목록 조회
  const fetchRegisteredAlerts = async () => {
    try {
      setIsLoading(true);
      
      // JWT 토큰 가져오기
      const token = localStorage.getItem('jwtToken') || user?.token;
      
      if (!token) {
        console.log('토큰이 없어 임시 데이터를 표시합니다.');
        // 토큰이 없을 때 임시 더미 데이터
        setTimeout(() => {
          setRegisteredAlerts([
            {
              id: 1,
              keyword: '주식',
              frequency: 'hourly',
              alertType: 'email',
              alertInfo: 'user@example.com',
              isActive: true,
              createdAt: '2025-09-11',
              lastAlert: '2025-09-11 14:00'
            }
          ]);
          setIsLoading(false);
        }, 1000);
        return;
      }

      // 실제 API 호출
      const alerts = await getNewsAlerts(token);
      setRegisteredAlerts(alerts);
      setIsLoading(false);
      
    } catch (error) {
      console.error('알림 목록 조회 실패:', error);
      setSubmitMessage('❌ 알림 목록을 불러오는데 실패했습니다.');
      
      // 에러 시 빈 목록으로 설정
      setRegisteredAlerts([]);
      setIsLoading(false);
    }
  };

  // 시간을 분 단위로 변환하는 유틸리티 함수
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 분을 시간 문자열로 변환하는 유틸리티 함수
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // 시간대 중복 검증 로직
  const validateTimeSlot = (startTime, endTime, excludeIndex = -1) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    // 시작 시간이 종료 시간보다 늦으면 안됨
    if (startMinutes >= endMinutes) {
      return '시작 시간이 종료 시간보다 이르거나 같아야 합니다.';
    }

    // 기존 시간대들과 중복 검사 (겹치는지 확인)
    for (let i = 0; i < formData.timeSlots.length; i++) {
      if (i === excludeIndex) continue; // 수정 중인 시간대는 제외
      
      const existingStart = timeToMinutes(formData.timeSlots[i].startTime);
      const existingEnd = timeToMinutes(formData.timeSlots[i].endTime);

      // 새 시간대가 기존 시간대와 겹치는지 확인
      // 겹치는 경우: (새 시작 < 기존 종료) && (새 종료 > 기존 시작)
      if (startMinutes < existingEnd && endMinutes > existingStart) {
        return `시간대가 기존 ${formData.timeSlots[i].startTime}~${formData.timeSlots[i].endTime}와 겹칩니다.`;
      }
    }

    return null; // 검증 성공
  };

  // 다음 시간대 자동 설정 (마지막 시간대의 endTime + 1분)
  const getNextTimeSlot = () => {
    if (formData.timeSlots.length === 0) {
      return { startTime: '09:00', endTime: '09:59' };
    }

    // 시간대를 시작 시간 순으로 정렬
    const sortedSlots = [...formData.timeSlots].sort((a, b) => 
      timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );
    
    // 마지막 시간대의 종료 시간에 1분 추가
    const lastSlot = sortedSlots[sortedSlots.length - 1];
    const lastEndMinutes = timeToMinutes(lastSlot.endTime);
    const nextStartMinutes = lastEndMinutes + 1;
    const nextEndMinutes = nextStartMinutes + 59; // 기본 1시간 (59분간)

    // 24시를 넘지 않도록 검증
    if (nextEndMinutes >= 24 * 60) {
      return { startTime: '23:00', endTime: '23:59' };
    }

    return {
      startTime: minutesToTime(nextStartMinutes),
      endTime: minutesToTime(nextEndMinutes)
    };
  };

  // 시간대 입력 핸들러
  const handleTimeSlotChange = (field, value) => {
    setCurrentTimeSlot(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeSlotError('');
  };

  // 시간대 추가 핸들러
  const handleAddTimeSlot = () => {
    const error = validateTimeSlot(currentTimeSlot.startTime, currentTimeSlot.endTime);
    
    if (error) {
      setTimeSlotError(error);
      return;
    }

    // 새 시간대 추가
    const newTimeSlots = [...formData.timeSlots, { ...currentTimeSlot }];
    
    // 시작 시간 순으로 정렬
    newTimeSlots.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    
    setFormData(prev => ({
      ...prev,
      timeSlots: newTimeSlots
    }));

    // 다음 시간대 자동 설정
    setCurrentTimeSlot(getNextTimeSlot());
    setTimeSlotError('');
  };

  // 시간대 삭제 핸들러
  const handleRemoveTimeSlot = (index) => {
    const newTimeSlots = formData.timeSlots.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      timeSlots: newTimeSlots
    }));

    // 삭제 후 다음 시간대 다시 계산
    const nextSlot = getNextTimeSlot();
    setCurrentTimeSlot(nextSlot);
  };

  // 입력 값 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setSubmitMessage('❌ 로그인이 필요합니다.');
      return;
    }

    if (!formData.keyword.trim()) {
      setSubmitMessage('❌ 키워드를 입력해주세요.');
      return;
    }

    if (!formData.alertInfo.trim()) {
      setSubmitMessage('❌ 알림 정보를 입력해주세요.');
      return;
    }

    // 사용자 정의 시간대 검증
    if (formData.frequency === 'custom') {
      if (formData.timeSlots.length === 0) {
        setSubmitMessage('❌ 사용자 정의 주기를 선택한 경우 최소 1개 이상의 시간대를 설정해야 합니다.');
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // JWT 토큰 가져오기
      const token = localStorage.getItem('jwtToken') || user?.token;
      
      if (!token) {
        setSubmitMessage('❌ 로그인이 필요합니다. 다시 로그인해주세요.');
        setIsSubmitting(false);
        return;
      }

      // 실제 API 호출
      await createNewsAlert(formData, token);
      
      setSubmitMessage('✅ 뉴스 알림이 등록되었습니다.');
      setFormData({
        keyword: '',
        frequency: 'hourly',
        alertType: 'email',
        alertInfo: '',
        isActive: true,
        timeSlots: []
      });
      
      // 시간대 입력도 초기화
      setCurrentTimeSlot({
        startTime: '09:00',
        endTime: '09:59'
      });
      
      // 알림 목록 새로고침
      await fetchRegisteredAlerts();
      setIsSubmitting(false);
      
      // 성공 메시지 3초 후 제거
      setTimeout(() => setSubmitMessage(''), 3000);

    } catch (error) {
      console.error('알림 등록 실패:', error);
      setSubmitMessage(`❌ 알림 등록에 실패했습니다: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  // 알림 삭제 핸들러
  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('이 알림을 삭제하시겠습니까?')) {
      return;
    }

    try {
      // JWT 토큰 가져오기
      const token = localStorage.getItem('jwtToken') || user?.token;
      
      if (!token) {
        setSubmitMessage('❌ 로그인이 필요합니다.');
        return;
      }

      // 실제 API 호출
      await deleteNewsAlert(alertId, token);
      
      // 로컬 상태 업데이트
      setRegisteredAlerts(prev => prev.filter(alert => alert.id !== alertId));
      setSubmitMessage('✅ 알림이 삭제되었습니다.');
      
      setTimeout(() => setSubmitMessage(''), 3000);
    } catch (error) {
      console.error('알림 삭제 실패:', error);
      setSubmitMessage(`❌ 알림 삭제에 실패했습니다: ${error.message}`);
      setTimeout(() => setSubmitMessage(''), 3000);
    }
  };

  // 알림 토글 핸들러
  const handleToggleAlert = async (alertId, currentStatus) => {
    try {
      // JWT 토큰 가져오기
      const token = localStorage.getItem('jwtToken') || user?.token;
      
      if (!token) {
        setSubmitMessage('❌ 로그인이 필요합니다.');
        return;
      }

      // 실제 API 호출
      await toggleNewsAlert(alertId, !currentStatus, token);

      // 로컬 상태 업데이트
      setRegisteredAlerts(prev => 
        prev.map(alert => 
          alert.id === alertId 
            ? { ...alert, isActive: !currentStatus }
            : alert
        )
      );
      
      setSubmitMessage(`✅ 알림이 ${!currentStatus ? '활성화' : '비활성화'}되었습니다.`);
      setTimeout(() => setSubmitMessage(''), 3000);
      
    } catch (error) {
      console.error('알림 상태 변경 실패:', error);
      setSubmitMessage(`❌ 알림 상태 변경에 실패했습니다: ${error.message}`);
      setTimeout(() => setSubmitMessage(''), 3000);
    }
  };

  // 로그인하지 않은 사용자
  if (!isAuthenticated) {
    return (
      <div className="news-alert-page">
        <div className="page-container">
          <div className="login-required">
            <h2>🔐 로그인이 필요합니다</h2>
            <p>뉴스 알림 서비스를 이용하려면 먼저 로그인해주세요.</p>
            <a href="/login" className="login-link">로그인하기</a>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="news-alert-page">
      <div className="page-container">
        <h1 className="page-title">
          <span className="page-icon">📰</span>
          뉴스 알림 서비스
        </h1>
        <p className="page-description">
          관심 있는 키워드의 뉴스를 자동으로 크롤링하여 알림을 받아보세요.
        </p>

        {/* 알림 등록 폼 */}
        <div className="alert-form-section">
          <h2>새 알림 등록</h2>
          <form onSubmit={handleSubmit} className="alert-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="keyword">키워드 *</label>
                <input
                  type="text"
                  id="keyword"
                  name="keyword"
                  value={formData.keyword}
                  onChange={handleInputChange}
                  placeholder="예: 삼성전자, AI, 주식시장"
                  maxLength={50}
                  required
                />
                <small>크롤링할 뉴스 키워드를 입력하세요</small>
              </div>

              <div className="form-group">
                <label htmlFor="frequency">알림 주기 *</label>
                <select
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  required
                >
                  <option value="realtime">실시간</option>
                  <option value="hourly">1시간마다</option>
                  <option value="daily">매일</option>
                  <option value="weekly">매주</option>
                  <option value="custom">사용자 정의 시간대</option>
                </select>
                <small>뉴스를 확인할 주기를 선택하세요</small>
              </div>
            </div>

            {/* 사용자 정의 시간대 입력 섹션 */}
            {formData.frequency === 'custom' && (
              <div className="time-slots-section">
                <h3>크롤링 시간대 설정</h3>
                <p className="time-slots-description">
                  크롤링할 시간대를 설정하세요. 시간대는 겹칠 수 없으며, 분 단위로 정확히 입력해야 합니다.
                </p>

                {/* 시간대 입력 폼 */}
                <div className="time-slot-input">
                  <div className="time-input-row">
                    <div className="time-input-group">
                      <label htmlFor="startTime">시작 시간</label>
                      <input
                        type="time"
                        id="startTime"
                        value={currentTimeSlot.startTime}
                        onChange={(e) => handleTimeSlotChange('startTime', e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <div className="time-separator">~</div>
                    <div className="time-input-group">
                      <label htmlFor="endTime">종료 시간</label>
                      <input
                        type="time"
                        id="endTime"
                        value={currentTimeSlot.endTime}
                        onChange={(e) => handleTimeSlotChange('endTime', e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddTimeSlot}
                      className="add-time-slot-button"
                      disabled={!currentTimeSlot.startTime || !currentTimeSlot.endTime}
                    >
                      <span className="add-icon">➕</span>
                      추가
                    </button>
                  </div>
                  
                  {timeSlotError && (
                    <div className="time-slot-error">
                      ❌ {timeSlotError}
                    </div>
                  )}
                  
                  <small className="time-slot-help">
                    💡 다음 시간대는 자동으로 이전 시간대 종료 + 1분으로 설정됩니다
                  </small>
                </div>

                {/* 등록된 시간대 목록 */}
                {formData.timeSlots.length > 0 && (
                  <div className="registered-time-slots">
                    <h4>등록된 시간대 ({formData.timeSlots.length}개)</h4>
                    <div className="time-slots-list">
                      {formData.timeSlots.map((slot, index) => (
                        <div key={index} className="time-slot-item">
                          <div className="time-slot-info">
                            <span className="time-slot-icon">🕐</span>
                            <span className="time-slot-text">
                              {slot.startTime} ~ {slot.endTime}
                            </span>
                            <span className="time-slot-duration">
                              ({Math.ceil((timeToMinutes(slot.endTime) - timeToMinutes(slot.startTime) + 1))}분)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveTimeSlot(index)}
                            className="remove-time-slot-button"
                            title="시간대 삭제"
                          >
                            <span className="remove-icon">🗑️</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="time-slots-summary">
                      총 크롤링 시간: {formData.timeSlots.reduce((total, slot) => 
                        total + (timeToMinutes(slot.endTime) - timeToMinutes(slot.startTime) + 1), 0
                      )}분
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="alertType">알림 방식 *</label>
                <select
                  id="alertType"
                  name="alertType"
                  value={formData.alertType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="email">이메일</option>
                  <option value="sms">SMS</option>
                  <option value="push">푸시 알림</option>
                  <option value="webhook">웹훅</option>
                </select>
                <small>알림을 받을 방식을 선택하세요</small>
              </div>

              <div className="form-group">
                <label htmlFor="alertInfo">알림 정보 *</label>
                <input
                  type="text"
                  id="alertInfo"
                  name="alertInfo"
                  value={formData.alertInfo}
                  onChange={handleInputChange}
                  placeholder={
                    formData.alertType === 'email' ? '이메일 주소' :
                    formData.alertType === 'sms' ? '전화번호' :
                    formData.alertType === 'webhook' ? '웹훅 URL' :
                    '알림 정보'
                  }
                  required
                />
                <small>
                  {formData.alertType === 'email' && '이메일 주소를 입력하세요'}
                  {formData.alertType === 'sms' && '전화번호를 입력하세요 (예: 010-1234-5678)'}
                  {formData.alertType === 'push' && '푸시 알림이 활성화된 기기로 전송됩니다'}
                  {formData.alertType === 'webhook' && '웹훅 URL을 입력하세요'}
                </small>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                알림 활성화
              </label>
              <small>체크 해제 시 알림이 전송되지 않습니다</small>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    등록 중...
                  </>
                ) : (
                  <>
                    <span className="submit-icon">➕</span>
                    알림 등록
                  </>
                )}
              </button>
            </div>

            {submitMessage && (
              <div className={`submit-message ${submitMessage.startsWith('✅') ? 'success' : 'error'}`}>
                {submitMessage}
              </div>
            )}
          </form>
        </div>

        {/* 등록된 알림 목록 */}
        <div className="alert-list-section">
          <h2>등록된 알림 목록</h2>
          
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>알림 목록을 불러오는 중...</p>
            </div>
          ) : registeredAlerts.length === 0 ? (
            <div className="empty-list">
              <div className="empty-icon">📭</div>
              <p>등록된 알림이 없습니다.</p>
              <small>위 폼을 통해 새로운 뉴스 알림을 등록해보세요.</small>
            </div>
          ) : (
            <div className="alert-cards">
              {registeredAlerts.map((alert) => (
                <div key={alert.id} className={`alert-card ${!alert.isActive ? 'inactive' : ''}`}>
                  <div className="alert-header">
                    <div className="alert-keyword">
                      <span className="keyword-icon">🔍</span>
                      <strong>{alert.keyword}</strong>
                    </div>
                    <div className="alert-status">
                      <button
                        className={`status-toggle ${alert.isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleAlert(alert.id, alert.isActive)}
                        title={alert.isActive ? '알림 비활성화' : '알림 활성화'}
                      >
                        {alert.isActive ? '🔔' : '🔕'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="alert-details">
                    <div className="detail-item">
                      <span className="detail-label">주기:</span>
                      <span className="detail-value">
                        {alert.frequency === 'realtime' && '실시간'}
                        {alert.frequency === 'hourly' && '1시간마다'}
                        {alert.frequency === 'daily' && '매일'}
                        {alert.frequency === 'weekly' && '매주'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">방식:</span>
                      <span className="detail-value">
                        {alert.alertType === 'email' && '📧 이메일'}
                        {alert.alertType === 'sms' && '📱 SMS'}
                        {alert.alertType === 'push' && '🔔 푸시'}
                        {alert.alertType === 'webhook' && '🔗 웹훅'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">정보:</span>
                      <span className="detail-value">{alert.alertInfo}</span>
                    </div>
                  </div>

                  <div className="alert-meta">
                    <small>등록일: {alert.createdAt}</small>
                    {alert.lastAlert && <small>최근 알림: {alert.lastAlert}</small>}
                  </div>

                  <div className="alert-actions">
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteAlert(alert.id)}
                      title="알림 삭제"
                    >
                      <span className="delete-icon">🗑️</span>
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsAlertPage;